"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const utils_1 = require("./utils");
class Prediction {
    constructor(prediction, closeLowThreshold = 0.25, closeHighThreshold) {
        this._closeHighThreshold = closeHighThreshold;
        this._closeLowThreshold = closeLowThreshold;
        if (prediction) {
            this.boundingBox = prediction.boundingBox;
            this.mesh = prediction.mesh;
            this.scaledMesh = prediction.scaledMesh;
            this.annotations = prediction.annotations;
            this.faceInViewConfidence = prediction.faceInViewConfidence;
        }
    }
    get pose() {
        if (!this._pose)
            this._pose = this._getPose(this.scaledMesh);
        return this._pose;
    }
    get eyes() {
        if (!this._eyes) {
            const eyesInfo = this._getEyesInfo(this.scaledMesh);
            this._eyes = eyesInfo;
        }
        return this._eyes;
    }
    getBase64Image(input) {
        const start = this.boundingBox.topLeft;
        const end = this.boundingBox.bottomRight;
        const [width, height] = [end[0] - start[0], end[1] - start[1]];
        const canvas = document.createElement('canvas');
        // extend top border
        const paddingTop = 0.2;
        const [sx, sy] = [start[0], Math.max(start[1] - height * paddingTop, 0)];
        const [sw, sh] = [width, Math.min(height + height * paddingTop + sy, input.height) - sy];
        canvas.width = sw;
        canvas.height = sh;
        const ctx = canvas.getContext('2d');
        ctx &&
            ctx.drawImage(input, sx, sy, sw, sh, 0, 0, width, sh);
        let dataURL = canvas.toDataURL('image/png');
        return dataURL.replace(/^data:image\/(png|jpg);base64,/, '');
    }
    // Eyes start
    _getEyesInfo(points) {
        const ellipticityRight = this._getEllipticity({ vStart: points[33], vEnd: points[133] }, { vStart: points[145], vEnd: points[159] });
        const ellipticityLeft = this._getEllipticity({ vStart: points[263], vEnd: points[362] }, { vStart: points[374], vEnd: points[386] });
        const maxEllipticityRight = this._getEllipticity({ vStart: points[33], vEnd: points[133] }, { vStart: points[475], vEnd: points[477] });
        const maxEllipticityLeft = this._getEllipticity({ vStart: points[263], vEnd: points[362] }, { vStart: points[470], vEnd: points[472] });
        const degreeEyeOpeningLeft = ellipticityLeft / maxEllipticityLeft;
        const degreeEyeOpeningRight = ellipticityRight / maxEllipticityRight;
        let closed = true;
        if (degreeEyeOpeningLeft < this._closeLowThreshold &&
            degreeEyeOpeningRight < this._closeLowThreshold) {
            closed = true;
        }
        let indentOpenEye = this._closeLowThreshold * 0.2;
        let openThreshold = this._closeHighThreshold
            ? this._closeHighThreshold
            : this._closeLowThreshold + indentOpenEye;
        if (degreeEyeOpeningLeft > openThreshold || degreeEyeOpeningRight > openThreshold) {
            closed = false;
        }
        return {
            isClosed: closed,
            levelOpeningEyes: {
                left: degreeEyeOpeningLeft,
                right: degreeEyeOpeningRight,
            },
        };
    }
    _getEllipticity(majorPoints, minorPoints) {
        const semiMajorAxis = utils_1.distance(majorPoints.vStart, majorPoints.vEnd) * 0.5;
        const semiMinorAxis = utils_1.distance(minorPoints.vStart, minorPoints.vEnd) * 0.5;
        const ellipticity = this._ellipticity(semiMajorAxis, semiMinorAxis);
        return ellipticity;
    }
    _ellipticity(a, b) {
        return 1 - (a - b) / a;
    }
    // Eyes end
    // Pose start
    _getPose(points) {
        const bottomFacePoint = this._getComputeMeanPoint(199, points);
        const v1 = {
            x: points[108][0] - bottomFacePoint.x,
            y: points[108][1] - bottomFacePoint.y,
            z: points[108][2] - bottomFacePoint.z,
        };
        const v2 = {
            x: points[337][0] - bottomFacePoint.x,
            y: points[337][1] - bottomFacePoint.y,
            z: points[337][2] - bottomFacePoint.z,
        };
        const normalVector = this._getNormalVector(v1, v2);
        const roll = this._getRoll(points);
        const yaw = (-Math.atan(normalVector.x / normalVector.z) * 180) / Math.PI;
        const pitch = (Math.atan(normalVector.y / normalVector.z) * 180) / Math.PI;
        return {
            roll,
            pitch: pitch < 0 ? pitch + 10 : pitch,
            yaw,
        };
    }
    _getNormalVector(v1, v2) {
        let x, y, z;
        x = v1.y * v2.z - v1.z * v2.y;
        y = -(v1.x * v2.z - v1.z * v2.x);
        z = v1.x * v2.y - v1.y * v2.x;
        return {
            x,
            y,
            z,
        };
    }
    _getComputeMeanPoint(pointIndexes, points) {
        let vector = {
            x: 0,
            y: 0,
            z: 0,
        };
        for (var i = 0; i < pointIndexes; i++) {
            vector.x += points[i][0];
            vector.y += points[i][1];
            vector.z += points[i][2];
        }
        vector.x = vector.x / pointIndexes;
        vector.y = vector.y / pointIndexes;
        vector.z = vector.z / pointIndexes;
        return vector;
    }
    _getRoll(points) {
        const rightEye = this._getAverageCoordinate(points, [385, 387, 380, 373]);
        const leftEye = this._getAverageCoordinate(points, [160, 158, 144, 153]);
        return (Math.atan2(rightEye.y - leftEye.y, rightEye.x - leftEye.x) * 180) / Math.PI;
    }
    _getAverageCoordinate(points, indexes) {
        let vector2D, dx = 0, dy = 0;
        indexes.forEach((index) => {
            dx += points[index][0];
            dy += points[index][1];
        });
        vector2D = {
            x: dx / indexes.length,
            y: dy / indexes.length,
        };
        return vector2D;
    }
}
exports.default = Prediction;
